import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { useParams } from "react-router-dom";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { CategorizedMapEntries, EntityPin, MultiPin } from "@mapmycustomers/shared/types/map";
import User from "@mapmycustomers/shared/types/User";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import { convertLatLngBoundsToPlatformBounds } from "@mapmycustomers/shared/util/geo/GeoService";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { UserLocation } from "@app/component/map/FeaturedMap";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import RecordStylingContext from "@app/component/RecordCard/utils/RecordStylingContext";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import BaseMapViewContext from "@app/component/view/utils/BaseMapViewContext";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import MarkerEffect from "@app/enum/MarkerEffect";
import EntityAnnotation from "@app/scene/map/components/EntityAnnotation";
import EntityLassoTool from "@app/scene/map/components/EntityLassoTool";
import EntityFeature from "@app/scene/map/components/features/EntityFeature";
import MultiPinFeature from "@app/scene/map/components/features/MultiPinFeature";
import defaultMultiPinStyleGetter from "@app/scene/map/components/features/MultiPinFeature/utils/defaultMultiPinStyleGetter";
import TerritoryFeature from "@app/scene/map/components/features/TerritoryFeature";
import { HeatMapLayer, TrafficLayer, UserLocationLayer } from "@app/scene/map/components/layers";
import Legends from "@app/scene/map/components/Legends";
import LocationFilterCircle from "@app/scene/map/components/LocationFilterCircle";
import CreateEditPinLegendDrawer from "@app/scene/map/components/pinLegends/CreateEditPinLegendDrawer";
import ShowOtherPins from "@app/scene/map/components/ShowOtherPins";
import LassoTool from "@app/scene/map/components/tools/LassoTool";
import ToolSelectionPanel from "@app/scene/map/components/ToolSelectionPanel";
import Legend from "@app/scene/map/enums/Legend";
import {
  enterTerritoryMode,
  fetchTerritoryLassoSelection,
  fetchTerritoryPins,
  initializeTerritoryMode,
  setPinHover,
  setSelectedPin,
  showAnnotation,
} from "@app/scene/map/store/actions";
import {
  areBoundariesVisible,
  getBoundaryType,
  getCountryCode,
} from "@app/scene/map/store/layers/boundaries/selectors";
import { MapModeAnnotationView } from "@app/scene/map/store/mapMode/MapModeState";
import {
  getAnnotation,
  getExternalTerritoryEntries,
  getLayerLegendsState,
  getMapViewTool,
  getTerritory,
  getTerritoryDataViewData,
  getTerritoryEntries,
  getTerritoryMapViewState,
  getTerritoryMode,
  getVisibleBadges,
  isTerritoryDataViewVisible,
} from "@app/scene/map/store/selectors";
import TerritoryModeState from "@app/scene/map/store/territoryMode/TerritoryModeState";
import { showEntityView } from "@app/store/entityView/actions";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories } from "@app/store/territories";
import colors from "@app/styles/_colors.module.scss";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import getContrastColor from "@app/util/colors/getContrastColor";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import { OTHER_PIN_ZINDEX } from "@app/util/map/consts";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import mergeFilterModels from "../../utils/mergeFilterModels";
import ClusterFeature from "../features/ClusterFeature";
import DemographyFeaturesLayer from "../features/DemographyFeature/DemographyFeaturesLayer";
import Toolbar from "../Toolbar";
import MapToolInformationPanel from "../tools/MapToolInformationPanel";

import CreateSidebar from "./components/CreateSidebar";
import DataViewWrapper from "./components/DataViewWrapper";
import EditSidebar from "./components/EditSidebar";
import TopPanel from "./components/TopPanel";
import styles from "./TerritoriesPage.module.scss";
import BoundsContext from "./utils/BoundsContext";
import useBoundsContextHandler from "./utils/useBoundsContextHandler";

const availableTools: MapTool[] = [MapTool.LASSO, MapTool.TAKE_SNAPSHOT];

// wait for max 500ms before triggering next fetchPins request
const boundsChangeOptions = { maxWait: 500 };

interface Props {
  annotation?: MapModeAnnotationView;
  boundariesVisible: boolean;
  boundaryType: BoundaryType;
  countryCode: CountryCode;
  dataViewVisible: boolean;
  initialize: typeof initializeTerritoryMode.request;
  lassoFetcher: typeof fetchTerritoryLassoSelection.request;
  loading?: boolean;
  mapEntries: CategorizedMapEntries;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  mapViewState: MapViewState;
  mode: TerritoryModeState["mode"];
  onEnterTerritoryMode: typeof enterTerritoryMode;
  onShowAnnotation: typeof showAnnotation.request;
  otherMapEntries: CategorizedMapEntries;
  pinsFetcher: typeof fetchTerritoryPins.request;
  selectedTool?: MapTool;
  setPinHover: typeof setPinHover;
  setSelectedPin: typeof setSelectedPin;
  showEntityView: typeof showEntityView;
  territories: Territory[];
  territory?: Territory;
  totalFilteredRecords: number;
  totalRecords: number;
  visibleBadges: AlertBadgesType[];
}

const TerritoriesPage: React.FC<Props> = ({
  annotation,
  boundariesVisible,
  boundaryType,
  countryCode,
  dataViewVisible,
  initialize,
  lassoFetcher,
  mapEntries,
  mapSettings,
  mapViewState,
  mode,
  onEnterTerritoryMode,
  onShowAnnotation,
  otherMapEntries,
  pinsFetcher,
  selectedTool,
  setPinHover,
  setSelectedPin,
  showEntityView,
  territories,
  territory,
  visibleBadges,
}) => {
  const { map } = useMap();

  const { territoryId } = useParams<{ territoryId: string }>();

  const analytics = useExtendedAnalytics(["Territory mode"], useAnalytics());
  useEffect(() => {
    analytics.initiated("");
  }, [analytics]);

  useEffect(() => {
    // initialization
    const bounds = map.getBounds();
    const zoom = map.getZoom();

    if (bounds && zoom) {
      initialize({
        callback: (bounds) => {
          // update map to fit all territory's pins
          // if bound only consists of one point, then don't change the zoom and just move to
          // that point
          if (bounds.isEmpty()) {
            // do nothing
          } else if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            map.setCenter(bounds.getCenter());
          } else {
            map.fitBounds(bounds);
          }
        },
        territoryId: territoryId !== "create" ? parseInt(territoryId, 10) : undefined,
        viewport: {
          bounds: convertLatLngBoundsToPlatformBounds(bounds),
          zoom,
        },
      });
    }
  }, [territoryId, initialize, map, pinsFetcher]);

  useMapEventHandler(
    map,
    // We don't use `idle` because it is not triggered if you stopped moving but keep mouse
    // button pressed. Hence, we use debounce to avoid calling backend too frequently.
    // However, we still use maxWait to guarantee that at least one request is sent every
    // maxWait milliseconds. This is needed to load data if user is panning continuously.
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        const bounds = this.getBounds();
        const zoom = this.getZoom();
        if (bounds && zoom) {
          pinsFetcher({
            request: {},
            viewport: { bounds: convertLatLngBoundsToPlatformBounds(bounds), zoom },
          });
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  const handleUpdateFilterModel: IMapViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: ModifiedMapFilterModel) => {
      let resultingFilterModel = mergeFilterModels(mapViewState.filter, newFilterModel);
      if (resultingFilterModel.universal === undefined) {
        const { universal } = mapViewState.filter;
        const updatedUniversalFilter = universal?.territories
          ? { territories: universal.territories }
          : undefined;
        resultingFilterModel = { ...resultingFilterModel, universal: updatedUniversalFilter };
      }
      pinsFetcher({ request: { filter: resultingFilterModel } });
    }
  );

  const handleUpdateViewState: IMapViewContext["updateViewState"] = useDynamicCallback(
    (viewState: Partial<MapViewState>) => {
      pinsFetcher({ request: viewState, updateOnly: true });
    }
  );

  const handleUpdateViewAs: IMapViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      pinsFetcher({ request: { viewAs: userId } });
    }
  );

  const context: IMapViewContext = useMemo(
    () => ({
      updateFilterModel: handleUpdateFilterModel,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: pinsFetcher,
      viewState: mapViewState,
    }),
    [handleUpdateFilterModel, handleUpdateViewAs, handleUpdateViewState, mapViewState, pinsFetcher]
  );

  const pinColorGetter = useCallback(() => {
    const fillColor = territory?.color ? anyColorToHex(territory.color) : colors.slate;
    return {
      color: getContrastColor(fillColor) === "black" ? colors.slate : colors.white,
      fillColor,
    };
  }, [territory?.color]);

  const territoryItemEffectGetter = useMemo(
    () => (entityPin: EntityPin) => {
      if (
        annotation?.entityId === entityPin.data.id &&
        annotation?.entityType === entityPin.data.entity
      ) {
        return MarkerEffect.OUTLINE;
      }
      return MarkerEffect.SHADOW;
    },
    [annotation]
  );

  const territoryItemStyleGetter = useMemo(
    () =>
      defaultEntityStyleGetter({
        areBadgesVisible: visibleBadges.length > 0,
        colorGetter: pinColorGetter,
        effect: territoryItemEffectGetter,
        markerSize: mapSettings.markerSize,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: mapSettings.showEntityIcons,
        visibleBadges,
      }),
    [mapSettings, pinColorGetter, territoryItemEffectGetter, visibleBadges]
  );

  const externalTerritoryItemStyleGetter = useMemo(
    () =>
      defaultEntityStyleGetter({
        areBadgesVisible: visibleBadges.length > 0,
        colorGetter: () => ({
          color: colors.white,
          fillColor: colors.disabledGray,
        }),
        effect: territoryItemEffectGetter,
        markerSize: mapSettings.markerSize,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: mapSettings.showEntityIcons,
        visibleBadges,
      }),
    [mapSettings, territoryItemEffectGetter, visibleBadges]
  );

  const multiPinStyleGetter = useMemo(
    () =>
      defaultMultiPinStyleGetter({
        colorGetter: pinColorGetter,
        markerSize: mapSettings.markerSize,
        shape: (multiPin: MultiPin) => multiPin.data.shape ?? MarkerShape.SQUARE,
      }),
    [mapSettings.markerSize, pinColorGetter]
  );

  const externalMultiPinStyleGetter = useMemo(
    () =>
      defaultMultiPinStyleGetter({
        colorGetter: () => ({
          color: colors.white,
          fillColor: colors.disabledGray,
        }),
        markerSize: mapSettings.markerSize,
        shape: (multiPin: MultiPin) => multiPin.data.shape ?? MarkerShape.SQUARE,
      }),
    [mapSettings.markerSize]
  );

  // all but hidden and current
  const visibleTerritories = useMemo(
    () =>
      boundaryType === BoundaryType.TERRITORY && boundariesVisible
        ? territories.filter(
            ({ id, territoryDetail }) => !territoryDetail.hidden && id !== territory?.id
          )
        : [],
    [boundariesVisible, boundaryType, territories, territory?.id]
  );

  const handleSelectTerritory = useCallback(
    (territory: Territory) => {
      onEnterTerritoryMode?.({
        territory,
        territoryId: territory.id,
      });
    },
    [onEnterTerritoryMode]
  );

  const handlePinClick = useCallback(
    (pin) => onShowAnnotation(mapPinIdGetter(pin)),
    [onShowAnnotation]
  );

  const handlePinDoubleClick = useCallback(
    (pin) => {
      setSelectedPin(pin);
      setPinHover(mapPinIdGetter(pin));
      showEntityView({
        entityId: pin.data.id,
        entityType: pin.data.entity,
      });
    },
    [showEntityView, setSelectedPin, setPinHover]
  );

  const boundsContext = useBoundsContextHandler();

  const handleToggleOtherPins = useCallback(
    (visible: boolean) => {
      pinsFetcher({ request: { showOther: visible } });
    },
    [pinsFetcher]
  );

  const isLassoMode = selectedTool === MapTool.LASSO;

  const recordStylingValue = useMemo(
    () => ({
      getPinStyle: defaultEntityStyleGetter({
        areBadgesVisible: visibleBadges.length > 0,
        colorGetter: mode === "view" ? pinColorGetter : undefined,
        effect: MarkerEffect.NONE,
        markerSize: MarkerSize.LARGE,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: true,
        visibleBadges,
      }),
    }),
    [mode, pinColorGetter, visibleBadges]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <RecordStylingContext.Provider value={recordStylingValue}>
        <BaseMapViewContext.Provider value={context}>
          <BoundsContext.Provider value={boundsContext}>
            <div className={styles.container}>
              <Toolbar />
              <ToolSelectionPanel availableTools={territoryId === "create" ? [] : availableTools} />
              {territoryId !== "create" && <MapToolInformationPanel />}
              <TopPanel />
              <div className={styles.bottomContainer}>
                <Legends
                  allowedLegends={[
                    Legend.COLOR_KEY,
                    Legend.SHAPE_KEY,
                    Legend.BOUNDARIES,
                    Legend.ALERTS,
                    Legend.HEAT_MAP,
                    ...(mode !== "view" ? [Legend.USER_LOCATIONS] : []),
                    Legend.TRAFFIC,
                  ]}
                  className={styles.legends}
                />
                {territoryId !== "create" && (
                  <ShowOtherPins
                    checked={mapViewState.showOther}
                    className={styles.otherPins}
                    onChange={handleToggleOtherPins}
                  />
                )}
              </div>
            </div>
            <ClusterFeature clusters={otherMapEntries.clusters} type="externalClusters" />
            <MultiPinFeature
              multiPins={otherMapEntries.multiPins}
              styleGetter={mode === "view" ? externalMultiPinStyleGetter : undefined}
              type={`externalMultiPins-${mode}`}
              zIndex={OTHER_PIN_ZINDEX}
            />
            <EntityFeature
              entityPins={otherMapEntries.entities}
              onClick={handlePinClick}
              onDoubleClick={handlePinDoubleClick}
              styleGetter={mode === "view" ? externalTerritoryItemStyleGetter : undefined}
              type={`externalEntities-${mode}`}
              zIndex={OTHER_PIN_ZINDEX}
            />
            <ClusterFeature clusters={mapEntries.clusters} />
            <MultiPinFeature
              multiPins={mapEntries.multiPins}
              styleGetter={mode === "view" ? multiPinStyleGetter : undefined}
              type={`multiPins-${mode}`}
            />
            <EntityFeature
              entityPins={mapEntries.entities}
              onClick={handlePinClick}
              onDoubleClick={handlePinDoubleClick}
              styleGetter={mode === "view" ? territoryItemStyleGetter : undefined}
              type={`entities-${mode}`}
            />
            <EntityAnnotation />
            <LocationFilterCircle />
            <LassoTool />
            <EntityLassoTool fetcher={lassoFetcher} />
            {territoryId === "create" ? <CreateSidebar /> : <EditSidebar />}
            <UserLocation />
            <HeatMapLayer />
            <UserLocationLayer />
            <TrafficLayer />
            <TerritoryFeature
              disabled={isLassoMode}
              onOpen={territoryId === "create" ? undefined : handleSelectTerritory}
              // fake id when creating territory to gray out all other territories
              selectedTerritoryId={territoryId === "create" ? 0 : territory?.id}
              territories={visibleTerritories}
              viewMode={mode === "view"}
            />
            {boundaryType === BoundaryType.DEMOGRAPHY && boundariesVisible && mode === "view" && (
              <DemographyFeaturesLayer countryCode={countryCode} disableLabel />
            )}
            <CreateEditPinLegendDrawer />
            {dataViewVisible && <DataViewWrapper />}
          </BoundsContext.Provider>
        </BaseMapViewContext.Provider>
      </RecordStylingContext.Provider>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  const areBadgesVisible = getLayerLegendsState(state)[Legend.ALERTS]?.visible;
  const visibleBadges = getVisibleBadges(state);

  return {
    annotation: getAnnotation(state),
    boundariesVisible: areBoundariesVisible(state),
    boundaryType: getBoundaryType(state),
    countryCode: getCountryCode(state),
    dataViewVisible: isTerritoryDataViewVisible(state) && !!getTerritoryDataViewData(state),
    mapEntries: getTerritoryEntries(state),
    mapSettings: getMapSettings(state),
    mapViewState: getTerritoryMapViewState(state),
    mode: getTerritoryMode(state),
    otherMapEntries: getExternalTerritoryEntries(state),
    selectedTool: getMapViewTool(state),
    territories: getTerritories(state),
    territory: getTerritory(state),
    visibleBadges: areBadgesVisible ? visibleBadges : [],
  };
};

const mapDispatchToProps = {
  initialize: initializeTerritoryMode.request,
  lassoFetcher: fetchTerritoryLassoSelection.request,
  onEnterTerritoryMode: enterTerritoryMode,
  onShowAnnotation: showAnnotation.request,
  pinsFetcher: fetchTerritoryPins.request,
  setPinHover,
  setSelectedPin,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerritoriesPage);
